import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSearchBooks } from '@/features/library/hooks/useSearchBooks';
import type { Book } from '@/features/library/api/books';
import BookCard from '@/features/library/components/BookCard';

const AuthorBooksPage: React.FC = () => {
  const navigate = useNavigate();
  const { author = '' } = useParams<{ author: string }>();
  const decodedAuthor = decodeURIComponent(author);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [allBooks, setAllBooks] = useState<Book[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const { data, isLoading, isFetching, error } = useSearchBooks({ 
    author: decodedAuthor, 
    page, 
    limit: 10 
  });

  // Update books when data changes
  useEffect(() => {
    if (data?.books) {
      if (page === 1) {
        setAllBooks(data.books);
      } else {
        setAllBooks(prev => {
          const existingIds = new Set(prev.map(b => b.book_id));
          const uniqueNew = data.books.filter(b => !existingIds.has(b.book_id));
          return [...prev, ...uniqueNew];
        });
      }
      setHasMore(data.pagination?.has_next ?? false);
    }
  }, [data, page]);

  // Infinite scroll observer
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    if (target.isIntersecting && hasMore && !isFetching) {
      setPage(prev => prev + 1);
    }
  }, [hasMore, isFetching]);

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  const carouselBooks = useMemo(() => allBooks.map((b) => ({
    book_id: b.book_id,
    title: b.title,
    author: b.author,
    cover_image_url: b.cover_image_url ?? null,
    topic: b.topic,
  })), [allBooks]);

  // Shimmer loading component
  const ShimmerGrid = ({ count = 6 }: { count?: number }) => (
     <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col">
          <div className="w-full aspect-[2/3] bg-gray-200 rounded-xl animate-pulse mb-2"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse mb-1 w-full"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f7fa' }} dir="rtl">
      <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-3 mb-4 bg-[#f5f7fa] shadow-sm pt-safe-top">
        <div className="flex items-center justify-between w-full mt-2">
        <button onClick={() => navigate(-1)} className="p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors shadow-sm border border-gray-200">
          <svg className="w-6 h-6 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"></path>
          </svg>
        </button>
        <h1 className="text-lg font-bold font-persian text-foreground">{`از ${decodedAuthor} بخوانید`}</h1>
        <span className="w-10" />
        </div>
      </div>

       <div className="px-4 pb-8">
        {/* Initial loading */}
        {isLoading && <ShimmerGrid count={8} />}
        
        {error && (
          <div className="text-center py-8 text-red-500 font-persian">خطا در دریافت کتاب‌های نویسنده</div>
        )}
        
        {!isLoading && !error && allBooks.length === 0 && (
          <div className="text-center py-8 text-muted-foreground font-persian">کتابی یافت نشد</div>
        )}
        
        {allBooks.length > 0 && (
          <>
             <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-6">
              {allBooks.map((b, index) => (
                <BookCard
                  key={b.book_id}
                  book={b}
                  onClick={() =>
                    navigate(`/library/book`, {
                      state: {
                        bookId: b.book_id,
                        source: 'author',
                        author: decodedAuthor,
                        books: carouselBooks,
                        initialIndex: index,
                      },
                    })
                  }
                />
              ))}
            </div>
            
            {/* Load more trigger */}
            <div ref={loadMoreRef} className="py-4">
              {isFetching && !isLoading && (
                 <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex flex-col">
                      <div className="w-full aspect-[2/3] bg-gray-200 rounded-xl animate-pulse mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthorBooksPage;
